import React, { useRef, useState, useEffect } from 'react';

import parse from 'html-react-parser';

import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { Base64 } from 'js-base64';
import { v4 } from 'uuid';

import { RemoveLuxuryHomesParams, RootState } from '@share/utils';
import { ILuxuryHome } from '@share/common-types';
import { Routes } from '@share/constants';

import './style.scss';
import { LuxuryHomesDetailsImagesCarrousel } from '../images-carrousel';

const MIN_HEIGHT = 60;
const MIN_HEIGHT_DESCRIPTION = 90;

interface IProps {
  home: ILuxuryHome;

  locationId: string | null;
  dates: { checkIn: string | null | undefined; checkOut: string | null | undefined } | null;
  guests: number | null;

  isList?: boolean;
}

export const LuxuryHomesPropertyCard: React.FC<IProps> = ({ home, locationId, dates, guests, isList = false }) => {

  const history = useHistory();

  const nameRef = useRef<HTMLSpanElement>(null);
  const descriptionRef = useRef<HTMLSpanElement>(null);
  const amenitiesRef = useRef<HTMLDivElement>(null);
  const descriptionPopupRef = useRef<HTMLDivElement>(null);

  const { placeId } = useParams<{ placeId: string }>();

  const { account } = useSelector((state: RootState) => state.loginStore);

  const [collapsed, setCollapsed] = useState(true);
  const [collapsedDescription, setCollapsedDescription] = useState(true);
  
  const goToDetails = (propertyId: number, roomId: string) => {
    RemoveLuxuryHomesParams();

    const listIdParam = isList ? `/${v4()}` : '';
    const placeIdParam = placeId ? `/${placeId}` : home?.location?.id ? `/${home?.location?.id}` : isList ? `/${v4()}` : '';
    const locationParams = locationId ? `location=${locationId}` : '';
    const datesParams = dates ? `checkIn=${dates?.checkIn}&checkOut=${dates?.checkOut}` : '';
    const guestParams = guests ? `guests=${guests}` : '';
    const searchParams = (locationParams || datesParams || guestParams) ? 
                              `${location.search ? '&' : '?'}${locationParams}&${datesParams}&${guestParams}` : 
                              '';

    const baseUrl = `/${account?.name}${Routes.LuxuryHomesDetails}`;
    const url = `${baseUrl}${listIdParam}${placeIdParam}/${propertyId}/${roomId}`;
            
    history.push(`${url}${location.search}${searchParams}`);
  };

  const isSpanMinHeight = nameRef.current && (nameRef.current?.offsetHeight || 0) < MIN_HEIGHT;
  const isDescriptionMinHeight = descriptionRef.current && (descriptionRef.current?.offsetHeight || 0) < MIN_HEIGHT_DESCRIPTION;
  
  // Add click outside handler for amenities section
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    
    // Don't close if click is on the amenities div or the stat label span
    if (amenitiesRef.current && 
          !amenitiesRef.current.contains(target) && 
          !(target instanceof Element && target.closest('.luxury-place__stat-label-link'))) {
      setCollapsed(true);
    }

    if (descriptionPopupRef.current && 
        !descriptionPopupRef.current.contains(target) && 
        !(target instanceof Element && target.closest('.luxury-place__description-label-link'))) {
      setCollapsedDescription(true);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const kitchenType = home?.units?.[0]?.kitchenType;
  const name = home?.units?.[0]?.name;
  const bedroomsCount = home?.units?.[0]?.bedroomsCount;
  const maxOccupancy = home?.units?.[0]?.maxOccupancy;
  const unitId = home?.units?.[0]?.id;

  const kitchenTypeValid = !!kitchenType && kitchenType !== 'Unknown';
  
  return (
    <div className="luxury-place__card">
      <LuxuryHomesDetailsImagesCarrousel home={home} />

      <div className="luxury-place__content">
        <h2 className="luxury-place__title"><Tooltip title={home.title.split(',')[0]}>{home.title.split(',')[0]}</Tooltip></h2>
        <h3>{home.city}, {home.country}</h3>


        {!!home.descriptions?.length && (
          <div className="luxury-place__description" style={{ position: 'relative' }}>
            <div style={{ height: '90px', overflow: 'hidden', position: 'relative' }}>
              <span ref={descriptionRef}>
                {home.descriptions.map((d, i) => <p key={i} style={{ marginBottom: 5 }}>{parse(d)}</p>)}
              </span>
            </div>

            {!isDescriptionMinHeight && (
              <label 
                style={{ cursor: 'pointer', position: 'absolute', bottom: -22, textDecoration: 'underline', right: 0, color: '#4C717B', fontSize: '12px' }} 
                className="luxury-place__description-label-link"
                onClick={() => setCollapsedDescription(!collapsedDescription)}
              >
                {collapsedDescription ? 'See more' : 'See less'}
              </label>)}

            {!!home.descriptions?.length && !isDescriptionMinHeight && !collapsedDescription && (
              <div ref={descriptionPopupRef} className="rooms-search__amenities-all">
                {home.descriptions.map((d, i) => <p key={`${i}-description`} style={{ marginBottom: 5 }}>{parse(d)}</p>)}
              </div>)}
          </div>)}

        {(name || kitchenTypeValid) && (
          <>
            <div className="luxury-place__content-separator"></div>

            <div className="luxury-place__details" style={{ marginBottom: 0, minHeight: '75px' }}>
              <div className="luxury-place__stats stats-details" style={{ position: 'relative' }}>
                {name && (
                  <div className="luxury-place__stat" style={{ overflow: 'hidden' }}>
                    <span className="luxury-place__stat-label" ref={nameRef}>
                      {parse(name || '')}

                      {!isSpanMinHeight && (
                        <label className="luxury-place__stat-label-link" onClick={() => setCollapsed(!collapsed)}>{collapsed ? 'See more' : 'See less'}</label>)}
                    </span>
                  </div>)}

                {name && !isSpanMinHeight && !collapsed && (
                  <div ref={amenitiesRef} className="rooms-search__amenities-all">
                    {parse(name || '')}
                  </div>)}

                {kitchenTypeValid && (
                  <div className="luxury-place__stat">
                    <span className="luxury-place__stat-label">{kitchenType}</span>
                  </div>)}
              </div>
            </div>
          </>)}

        <div className="luxury-place__content-separator"></div>

        <div className="luxury-place__details">
          <div className="luxury-place__stats">
            <div className="luxury-place__stat">
              <span className="luxury-place__stat-value">{bedroomsCount || '0'}</span>
              <span className="luxury-place__stat-label">ROOMS</span>
            </div>
            <div className="luxury-place__stat">
              <span className="luxury-place__stat-value">{maxOccupancy || '0'}</span>
              <span className="luxury-place__stat-label">SLEEPS</span>
            </div>
          </div>
        </div>

        <button className="luxury-place__cta" onClick={() => goToDetails(home.id, Base64.encode(unitId))}>View Home</button>
      </div>
    </div>
  );
};
